
import React, { useEffect, useState } from "react";
import Breadcrums from "../../../components/breadcrums";
import { ikatAccountRecievableBreadcrums, resourceAccountRecievableBreadcrums } from "../../../constants/breadcrums";
import { paymentsCommands, sectionClass } from "../../../constants/common";
import PageTitle from "../../../components/pageTitle";
import { ColumnDef, ColumnFiltersState, FilterFn, GroupingState, getCoreRowModel, getExpandedRowModel, getFacetedMinMaxValues, getFacetedRowModel, getFacetedUniqueValues, getFilteredRowModel, getGroupedRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { getFinanceReportDataByIkat } from "../../../api/finance-api";
import { Link } from "react-router-dom";
import PortalTable from "../../../components/portalTable";
import { rankItem } from "@tanstack/match-sorter-utils";
import CommonFilters from "../../../blocks/commonFilters";
import { useApi } from "../../../api/data-hook";
import { useSnackbar } from "react-simple-snackbar";
import { listIkatEmployees } from "../../../api/consultants-api";
import Currency from "react-currency-formatter";
import classNames from "classnames";
import TanstackTable from "../../../components/TanstackTable";
import { FinanceRecord } from "../../../model";
import { NumericFormat } from "react-number-format";

// Utility function to sort by name ascending and month descending
function sortByNameAndMonth(data: any[]) {
    return data.slice().sort((a, b) => {
        // Sort by name ascending
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        // If names are equal, sort by month descending
        if (a.month < b.month) return 1;
        if (a.month > b.month) return -1;
        return 0;
    });
}

export default function AccountReceivableIkat() {
    const [isLoading, setLoading] = React.useState(false);

    const [people, setPeople] = useState([]);
    const [openSnackbar] = useSnackbar();

    const [data, setData] = React.useState<FinanceRecord[]>([]);
    const [grouping, setGrouping] = React.useState<GroupingState>([]);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = React.useState("");
    const [isConsultatnVisible, setisConsultatnVisible] = React.useState(false);
    const [columnVisibilityState, setColumnVisibilityState] = React.useState({});

    const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
        // Rank the item
        const itemRank = rankItem(row.getValue(columnId), value);

        // Store the itemRank info
        addMeta({ itemRank });
        // Return if the item should be filtered in/out
        return itemRank.passed;
    };

    const [showUnpaidOnly, setShowUnpaidOnly] = React.useState(false);
    const filteredData = showUnpaidOnly ? data.filter(row => Number(row.diff) !== 0) : data;

    const columns = React.useMemo<ColumnDef<FinanceRecord, any>[]>(
        () => [
            {
                accessorKey: "name",
                header: "Resource Name",
                // cell: (info) => info.getValue(),
                cell: ({ row }) => (
                    <>
                        <span className="font-bold">{row.original.name}</span>
                        {" (" + (row.original.project_name || row.original.project_pid || "IKAT") + ")"}
                    </>
                ),
                enableColumnFilter: false,
                enableGrouping: false,
                // enableSorting: true,
                footer: ({ table }) => (
                    <>
                        <span className="font-semibold text-md">Total</span>
                    </>
                ),
            },

            {
                accessorKey: "month",
                header: "Month",
                cell: (info) => info.getValue(),
                enableColumnFilter: false,
                enableGrouping: false,
                // enableSorting: true,
                footer: ({ table }) => <></>,
            },

            {
                accessorKey: "rate",
                header: "Rate (USD)",
                cell: (info) => <Currency quantity={Number(info.getValue()) || 0} currency="USD" />,
                enableGrouping: false,
                enableColumnFilter: false,
                meta: {
                    cellClassName: "text-right",
                },
            },
            {
                accessorKey: "hrs",
                header: "Efforts (Hrs)",
                cell: (info) => Number(info.getValue()).toFixed(2),
                enableGrouping: false,
                enableColumnFilter: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => (
                    <>
                        <NumericFormat prefix={""} value={Number(table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.hrs, 0)) || 0} allowLeadingZeros displayType="text" thousandSeparator="," />
                    </>
                ),
            },
            // {
            //     accessorKey: "month",
            //     header: "Month",
            //     cell: (info) => info.getValue(),
            //     // enableHiding: false,
            //     enableColumnFilter: false,
            //     enableGrouping: false,
            //     // columnVisibility: false,
            //     meta: {
            //         cellClassName: "text-center",
            //     },
            // },
            {
                accessorKey: "bill",
                header: "Bill Amount (USD)",
                // cell: (info) => <Currency quantity={Number(info.getValue()) || 0} currency="USD" />,
                cell: (info) => {
                    console.log('raw rate:', info.getValue()); // check this
                    return <Currency quantity={Number(info.getValue()) || 0} currency="USD" />;
                },
                // enableHiding: false,
                enableColumnFilter: false,
                enableGrouping: false,
                // columnVisibility: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => (
                    <>
                        {/* <span className="text-xs font-normal">Total Bill</span>
                        <br /> */}
                        <Currency quantity={Number(table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.bill, 0)) || 0} currency="USD" />
                    </>
                ),
            },
            {
                accessorKey: "payment",
                header: "Amount Received (USD)",
                cell: (info) => <Currency quantity={Number(info.getValue()) || 0} currency="USD" />,
                // enableHiding: false,
                enableColumnFilter: false,
                enableGrouping: false,
                // columnVisibility: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => (
                    <>
                        {/* <span className="text-xs font-normal">Total Payment Received</span>
                        <br /> */}
                        <Currency quantity={Number(table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.payment, 0)) || 0} currency="USD" />
                    </>
                ),
            },
            {
                accessorKey: "balance_hrs",
                header: "Balance Hrs",
                cell: ({ row }) => {
                    const diff = Number(row.original.diff) || 0;
                    if (diff === 0) return 0;
                    const value = Number(row.original.balance_hrs) || 0;
                    return Math.abs(value) < 1e-10 ? 0 : value;
                },
                // enableHiding: false,
                enableColumnFilter: false,
                enableGrouping: false,
                // columnVisibility: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => {
                    const total = table.getFilteredRowModel().rows.reduce((total, row) => {
                        const diff = Number(row.original.diff) || 0;
                        if (diff === 0) return total;
                        return total + (Number(row.original.balance_hrs) || 0);
                    }, 0);
                    const displayTotal = Math.abs(total) < 1e-10 ? 0 : total;
                    return <>{displayTotal}</>;
                },
            },
            {
                accessorKey: "diff",
                header: "Balance",
                cell: (info) => <Currency quantity={Number(info.getValue()) || 0} currency="USD" />,
                // enableHiding: false,
                enableColumnFilter: false,
                enableGrouping: false,
                // columnVisibility: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => (
                    <>
                        {/* <span className="text-xs font-normal">Total Dues</span>
                        <br /> */}
                        <Currency quantity={Number(table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.diff, 0)) || 0} currency="USD" />
                    </>
                ),
            },

            {
                accessorKey: "billMonth",
                header: "Billed Month",
                cell: (info) => info.getValue() || "-",

                enableColumnFilter: false,
                enableGrouping: false,
                // enableSorting: true,
                footer: ({ table }) => <></>,
            },

            {
                accessorKey: "invoiceDueMonth",
                header: "Due Month",
                cell: (info) => info.getValue() || "-",
                enableColumnFilter: false,
                enableGrouping: false,
                // enableSorting: true,
                footer: ({ table }) => <></>,
            },
        ],
        []
    );

    const table = useReactTable({
        data,
        columns,
        filterFns: {
            fuzzy: fuzzyFilter,
        },
        initialState: {
            // columnVisibility: {
            //     consultant: false,
            // },
            // hiddenColumns: columns.filter((col) => col.show === false).map((col) => col.accessor),
            // hiddenColumns: ["accesorName"],
            // hiddenColumns: columns.filter((column) => !column?.isVisible).map((column) => column.accessor),
        },
        state: {
            columnVisibility: columnVisibilityState,
            grouping,
            columnFilters,
            globalFilter,
        },
        onGroupingChange: setGrouping,
        getExpandedRowModel: getExpandedRowModel(),
        getGroupedRowModel: getGroupedRowModel(),
        getCoreRowModel: getCoreRowModel(),
        // getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: fuzzyFilter,
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        // debugTable: true,
        // debugHeaders: true,
        // debugColumns: true,
    });

    const data1 = (groupCol: string[], visibility: any) => {
        console.log(visibility);
        console.log(groupCol);

        table.setColumnVisibility(visibility);
        setColumnVisibilityState(visibility);
        setGrouping(groupCol);
    };

    useEffect(() => {
        setLoading(true);

        setPeople([]);
        setData([]);


        getFinanceReportDataByIkat({
            checkDate: new Date(),
            employee: "",
        }).then((data) => {
            setData(sortByNameAndMonth(data));
        });

        listIkatEmployees()
            .then((data) => setPeople(data))
            .finally(() => {
                setLoading(false);
            });
    }, []);

    // ...existing code...

    function applyFilters(filters): any {
        console.log(filters);

        setLoading(true);
        setData([]);

        getFinanceReportDataByIkat(filters)
            .then((data) => {
                setData(sortByNameAndMonth(data));
            })
            .finally(() => {
                setLoading(false);
            });
    }

    return (
        <main>
            <Breadcrums breadcrumLinks={ikatAccountRecievableBreadcrums} commands={paymentsCommands} groups={["Statement"]} module={"Payment"} />
            <PageTitle title="Ikat Rate Account Receivable Statement " preSummary="Comprehensive Report" summary="Analyzing Account Receivables, Payments, Settlements, and Dues for Strategic Insights."></PageTitle>

            <div className={classNames(sectionClass)}>
                <CommonFilters people={people} applyFilters={applyFilters} clientId={null} />

                {/* Unpaid Invoice Filter Toggle */}
                <div style={{ marginBottom: '1rem' }}>
                    <label>
                        <input
                            type="checkbox"
                            checked={showUnpaidOnly}
                            onChange={() => setShowUnpaidOnly((prev) => !prev)}
                            style={{ marginRight: '0.5rem' }}
                        />
                        Show only unpaid invoices
                    </label>
                </div>

                {/* <PortalTable table={table} handler={data1} /> */}
                {filteredData && filteredData.length > 0 && <TanstackTable columns={columns} data={filteredData} downloadBtn={true}></TanstackTable>}
                {!isLoading && filteredData.length <= 0 && <div className="my-5">No records found</div>}
                {isLoading && <div className="my-5">Loading... </div>}
            </div>
        </main>
    );

// ...existing code...
}
